"use client";

import { useState } from "react";
import { Button } from "@/scott/ui/elements/button/button";
import { Input } from "@/scott/ui/elements/input/input";
import { TextD2C } from "@/scott/ui/elements/text-d2c/text";
import type {
  StockNotificationActionResponse,
  stockNotificationAction,
} from "../stock-notification-action";

interface ProductStockNotificationInlineProps {
  action: typeof stockNotificationAction;
  language: string;
  productCode: string;
  dealerLocatorUrl: string;
  dealerLocatorLabel: string;
  microcopies: {
    title: string;
    notificationTitle: string;
    emailLabel: string;
    emailPlaceholder: string;
    submitLabel: string;
    successMessage: string;
    errorMessage: string;
  };
}

interface ProductStockNotificationInlineState {
  emailAddress: string;
  isSubmitting: boolean;
  errorMessage?: string;
  successMessage?: string;
}

function getProductUrlWithVariant(productCode: string): string {
  if (typeof window === "undefined") {
    return "";
  }

  const productUrl = new URL(window.location.href);
  productUrl.searchParams.set("article", productCode);

  return productUrl.toString();
}

export function ProductStockNotificationInline(
  props: Readonly<ProductStockNotificationInlineProps>
) {
  const [state, setState] = useState<ProductStockNotificationInlineState>({
    emailAddress: "",
    isSubmitting: false,
  });

  function updateState(
    nextState: Partial<ProductStockNotificationInlineState>
  ): void {
    setState((prevState) => ({
      ...prevState,
      ...nextState,
    }));
  }

  async function handleSubmit(): Promise<void> {
    if (state.isSubmitting) {
      return;
    }

    const emailAddress = state.emailAddress.trim();

    if (!emailAddress) {
      updateState({
        errorMessage: props.microcopies.errorMessage,
        successMessage: undefined,
      });
      return;
    }

    updateState({
      isSubmitting: true,
      errorMessage: undefined,
      successMessage: undefined,
    });

    let response: StockNotificationActionResponse;

    try {
      response = await props.action({
        emailAddress,
        language: props.language,
        productCode: props.productCode,
        productURL: getProductUrlWithVariant(props.productCode),
      });
    } catch {
      updateState({
        isSubmitting: false,
        errorMessage: props.microcopies.errorMessage,
        successMessage: undefined,
      });
      return;
    }

    if (response.status === "error") {
      updateState({
        isSubmitting: false,
        errorMessage: response.errorMessage ?? props.microcopies.errorMessage,
        successMessage: undefined,
      });
      return;
    }

    updateState({
      isSubmitting: false,
      emailAddress: "",
      errorMessage: undefined,
      successMessage: props.microcopies.successMessage,
    });
  }

  return (
    <section className="rounded-md bg-zinc-100 p-4">
      <div className="mb-3">
        <TextD2C style="bold" variant="p-small">
          {props.microcopies.title}
        </TextD2C>
      </div>

      <Button
        className="mb-3 h-9 w-full rounded-md bg-black text-white hover:bg-black"
        href={props.dealerLocatorUrl}
        target="_blank"
        type="button"
        unstyled
      >
        {props.dealerLocatorLabel}
      </Button>

      <div className="mb-3">
        <TextD2C variant="p-small">
          {props.microcopies.notificationTitle}
        </TextD2C>
      </div>

      <div className="flex items-center gap-2">
        <label className="sr-only" htmlFor="stock-notification-inline-email">
          {props.microcopies.emailLabel}
        </label>
        <Input
          className="h-9 border-zinc-300 bg-zinc-100 shadow-none"
          id="stock-notification-inline-email"
          onChange={(event) =>
            updateState({
              emailAddress: event.target.value,
              errorMessage: undefined,
              successMessage: undefined,
            })
          }
          placeholder={props.microcopies.emailPlaceholder}
          type="email"
          value={state.emailAddress}
        />
        <Button
          aria-label={props.microcopies.submitLabel}
          className="h-9 min-w-9 border-zinc-300 bg-zinc-100 px-3 text-foreground shadow-none hover:bg-zinc-100"
          loading={state.isSubmitting}
          onClick={handleSubmit}
          type="button"
          variant="outline-no-hover"
        >
          {">"}
        </Button>
      </div>

      {state.errorMessage && (
        <p aria-live="polite" className="mt-2 text-destructive text-xs">
          {state.errorMessage}
        </p>
      )}

      {state.successMessage && (
        <p aria-live="polite" className="mt-2 text-green-700 text-xs">
          {state.successMessage}
        </p>
      )}
    </section>
  );
}
